import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar as CalendarIcon, AlertCircle, CheckCircle2, Phone } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { BOOKING } from "@/constants/testIds";
import { COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    registration: "",
    vehicle: "",
    work_required: "",
    referral_source: "",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  // Earliest selectable = 14 days from today, advanced to next working day if needed
  const minDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 14);
    while (d.getDay() === 0 || d.getDay() === 6) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }, []);

  const isDisabled = (d) => {
    const day = d.getDay(); // 0 Sun ... 6 Sat
    if (day === 0 || day === 6) return true;
    if (d < minDate) return true;
    return false;
  };

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!date) {
      setError("Please choose a requested date.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/booking`, {
        ...form,
        referral_source: form.referral_source || null,
        requested_date: toISODate(date),
      });
      if (res.status === 201 || res.data?.success) {
        setSubmitted(true);
        toast.success("Booking request received — we'll be in touch shortly.");
        setForm({
          name: "", email: "", phone: "", registration: "",
          vehicle: "", work_required: "", referral_source: "", notes: "",
        });
        setDate(null);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your booking. Please call us on " +
          COMPANY.phone;
      setError(typeof msg === "string" ? msg : "Booking failed. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="booking" className="surface p-6 sm:p-10">
      <div className="mb-8">
        <div className="overline mb-3">Book Your Vehicle In</div>
        <h3 className="font-display text-2xl sm:text-3xl font-medium tracking-tight">
          Request a booking date.
        </h3>
        <p className="text-sm text-[var(--owrs-text-muted)] mt-3 leading-relaxed">
          To help us manage the diary, online bookings are available{" "}
          <span className="text-[var(--owrs-text)]">Monday – Friday</span>, at least{" "}
          <span className="text-[var(--owrs-text)]">two weeks in advance</span>. For more
          urgent work, please{" "}
          <a
            href={`tel:${COMPANY.phoneTel}`}
            className="text-[var(--owrs-accent)] underline-offset-4 hover:underline"
          >
            call us directly on {COMPANY.phone}
          </a>
          .
        </p>
      </div>

      {submitted ? (
        <div
          data-testid={BOOKING.successAlert}
          className="border border-[var(--owrs-accent)]/40 bg-[var(--owrs-accent)]/5 p-6 flex gap-4"
        >
          <CheckCircle2 className="h-6 w-6 text-[var(--owrs-accent)] shrink-0" />
          <div>
            <div className="font-display text-lg mb-1 uppercase">Booking request received</div>
            <p className="text-sm text-[var(--owrs-text-muted)]">
              Thanks — we've logged your request and will be in touch to confirm. For
              anything urgent in the meantime, please call {COMPANY.phone}.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="mt-4 text-xs tracking-wide text-[var(--owrs-accent)] hover:underline"
            >
              Submit another booking →
            </button>
          </div>
        </div>
      ) : (
        <form data-testid={BOOKING.form} onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="owrs-label" htmlFor="bk-name">Name</label>
            <input
              id="bk-name" name="name" value={form.name} onChange={onChange}
              data-testid={BOOKING.name} required maxLength={120}
              className="owrs-input" placeholder="Your full name"
            />
          </div>
          <div>
            <label className="owrs-label" htmlFor="bk-phone">Phone</label>
            <input
              id="bk-phone" name="phone" type="tel" value={form.phone} onChange={onChange}
              data-testid={BOOKING.phone} required
              className="owrs-input" placeholder="07…"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="owrs-label" htmlFor="bk-email">Email</label>
            <input
              id="bk-email" name="email" type="email" value={form.email} onChange={onChange}
              data-testid={BOOKING.email} required
              className="owrs-input" placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="owrs-label" htmlFor="bk-reg">Registration</label>
            <input
              id="bk-reg" name="registration" value={form.registration} onChange={onChange}
              data-testid={BOOKING.reg} required maxLength={20}
              className="owrs-input uppercase" placeholder="AB12 CDE"
              style={{ textTransform: "uppercase" }}
            />
          </div>
          <div>
            <label className="owrs-label" htmlFor="bk-vehicle">Make / Model (optional)</label>
            <input
              id="bk-vehicle" name="vehicle" value={form.vehicle} onChange={onChange}
              data-testid={BOOKING.vehicle}
              className="owrs-input" placeholder="e.g. Tesla Model 3 LR"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="owrs-label" htmlFor="bk-work">Work Required</label>
            <textarea
              id="bk-work" name="work_required" rows={4} value={form.work_required}
              onChange={onChange} data-testid={BOOKING.work} required maxLength={4000}
              className="owrs-input" placeholder="Describe the work you'd like us to carry out."
            />
          </div>

          <div className="sm:col-span-2">
            <label className="owrs-label">Requested Date</label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={BOOKING.dateTrigger}
                  className="owrs-input text-left flex items-center justify-between"
                >
                  <span className={date ? "text-[var(--owrs-text)]" : "text-[var(--owrs-text-dim)]"}>
                    {date ? formatDate(date) : "Select a working day, 14+ days ahead"}
                  </span>
                  <CalendarIcon className="h-4 w-4 text-[var(--owrs-accent)]" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="bg-[var(--owrs-surface)] border border-[var(--owrs-border-strong)] p-0 text-[var(--owrs-text)]"
              >
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setPickerOpen(false);
                  }}
                  disabled={isDisabled}
                  fromDate={minDate}
                  initialFocus
                  weekStartsOn={1}
                  className="bg-[var(--owrs-surface)]"
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-[var(--owrs-text-dim)] mt-2">
              Earliest available: {formatDate(minDate)}. Weekends are unavailable.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="owrs-label" htmlFor="bk-referral">How did you find us? (optional)</label>
            <select
              id="bk-referral" name="referral_source" value={form.referral_source}
              onChange={onChange} data-testid="booking-referral-select"
              className="owrs-input"
            >
              <option value="">Please select…</option>
              <option value="Google">Google</option>
              <option value="Social media">Social media</option>
              <option value="Word of mouth">Word of mouth</option>
              <option value="Returning customer">Returning customer</option>
              <option value="Flyer">Flyer</option>
              <option value="Golf course advertisement">Golf course advertisement</option>
              <option value="Local advert magazine">Local advert magazine</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="owrs-label" htmlFor="bk-notes">Anything else? (optional)</label>
            <textarea
              id="bk-notes" name="notes" rows={3} value={form.notes} onChange={onChange}
              data-testid={BOOKING.notes} maxLength={2000}
              className="owrs-input" placeholder="Symptoms, fault codes, history…"
            />
          </div>

          {error && (
            <div
              data-testid={BOOKING.errorAlert}
              className="sm:col-span-2 border border-red-500/40 bg-red-500/5 p-4 flex gap-3 items-start"
            >
              <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
              <div className="text-sm text-[var(--owrs-text)]">{error}</div>
            </div>
          )}

          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 mt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid={BOOKING.submit}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Request Booking"}
            </button>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              className="inline-flex items-center gap-2 text-sm text-[var(--owrs-text-muted)] hover:text-[var(--owrs-accent)]"
            >
              <Phone className="h-4 w-4" />
              Urgent? Call {COMPANY.phone}
            </a>
          </div>
        </form>
      )}
    </div>
  );
}
